import { useLiveQuery } from 'dexie-react-hooks'
import {
  BarChart3,
  ClipboardList,
  Compass,
  Drumstick,
  LayoutDashboard,
  type LucideIcon,
  Menu,
  MessageCircle,
  Play,
  ReceiptText,
  RotateCcw,
  Truck,
  Users,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { TABS, type TabKey, useApp } from '@/context'
import { getSetari } from '@/db/queries'
import { COMPANIE } from '@/db/seed'
import { resetDemo } from '@/db/seed'
import { cn } from '@/lib/utils'

const TAB_ICON: Record<TabKey, LucideIcon> = {
  panou: LayoutDashboard,
  mesaje: MessageCircle,
  comenzi: ClipboardList,
  clienti: Users,
  catalog: Drumstick,
  flota: Truck,
  facturare: ReceiptText,
  rapoarte: BarChart3,
}

function BrandMark({ small = false }: { small?: boolean }) {
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center rounded-lg bg-primary font-display font-semibold text-primary-foreground shadow-paper',
        small ? 'size-8 text-base' : 'size-9 text-lg',
      )}
      aria-hidden
    >
      E
    </span>
  )
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { activeTab, setActiveTab } = useApp()
  return (
    <nav className="flex flex-col gap-0.5">
      {TABS.map((tab) => {
        const Icon = TAB_ICON[tab.key]
        const active = activeTab === tab.key
        return (
          <button
            key={tab.key}
            type="button"
            data-tour={`nav-${tab.key}`}
            onClick={() => {
              setActiveTab(tab.key)
              onNavigate?.()
            }}
            className={cn(
              'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer',
              active
                ? 'bg-sidebar-accent text-foreground'
                : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-foreground',
            )}
          >
            <span
              className={cn(
                'absolute left-0 h-5 w-1 rounded-r-full bg-primary transition-opacity',
                active ? 'opacity-100' : 'opacity-0',
              )}
              aria-hidden
            />
            <Icon className={cn('size-4 shrink-0', active ? 'text-amber-ink' : 'text-taupe')} />
            {tab.label}
          </button>
        )
      })}
    </nav>
  )
}

function SidebarActions({ onAct }: { onAct?: () => void }) {
  const { runDemo, startTour, setActiveTab } = useApp()

  const onReset = async () => {
    await resetDemo()
    setActiveTab('panou')
    onAct?.()
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        size="lg"
        className="w-full justify-center"
        data-tour="btn-simuleaza"
        onClick={() => {
          runDemo()
          onAct?.()
        }}
      >
        <Play className="size-4" />
        Simulează o comandă
      </Button>
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            startTour()
            onAct?.()
          }}
        >
          <Compass className="size-4" />
          Tur ghidat
        </Button>
        <Button variant="ghost" size="sm" onClick={onReset}>
          <RotateCcw className="size-4" />
          Resetează
        </Button>
      </div>
    </div>
  )
}

function BrandBlock() {
  const setari = useLiveQuery(getSetari)
  const companie = setari?.companie ?? COMPANIE.companie
  const an = setari?.anInfiintare ?? COMPANIE.anInfiintare
  const tagline = setari?.tagline ?? COMPANIE.tagline
  return (
    <div className="flex items-start gap-3">
      <BrandMark />
      <div className="min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="font-display text-lg font-semibold leading-tight text-amber-ink">
            {companie}
          </span>
        </div>
        <div className="text-[0.7rem] font-medium uppercase tracking-wide text-taupe">din {an}</div>
        <p className="mt-1 font-display text-xs italic text-muted-foreground">{tagline}</p>
      </div>
    </div>
  )
}

export function Sidebar() {
  const [open, setOpen] = useState(false)

  return (
    <>
      {/* desktop rail */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar px-4 py-5 lg:flex">
        <div className="px-1">
          <BrandBlock />
        </div>
        <div className="mt-7 flex-1 overflow-y-auto">
          <NavList />
        </div>
        <div className="pt-4">
          <SidebarActions />
        </div>
      </aside>

      {/* mobile top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-sidebar-border bg-sidebar/95 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex items-center gap-2.5">
          <BrandMark small />
          <span className="font-display text-base font-semibold text-amber-ink">
            {COMPANIE.companie}
          </span>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" aria-label="Meniu">
              <Menu className="size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <div className="flex h-full flex-col px-4 py-5">
              <BrandBlock />
              <div className="mt-7 flex-1 overflow-y-auto">
                <NavList onNavigate={() => setOpen(false)} />
              </div>
              <div className="pt-4">
                <SidebarActions onAct={() => setOpen(false)} />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </header>
    </>
  )
}
